// KYC Status Indicator Component
// Shows KYC status and requirements based on user's region

import React from 'react';
import { 
  Box, 
  Chip, 
  Typography, 
  Alert,
  AlertTitle,
  Button,
  Paper
} from '@mui/material';
import {
  VerifiedUser as VerifiedIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  HourglassEmpty as PendingIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { shouldShowKYC, getKYCMessage } from '../utils/kycUtils';

const KYCStatusIndicator = ({ 
  userProfile, 
  kycStatus, 
  onStartKYC, 
  compact = false,
  makeOptional = false  // If true, makes KYC optional even if normally required (e.g., when PayPal is available)
}) => {
  const [kycInfo, setKycInfo] = useState({ show: false, required: false, reason: '' });
  const [kycMessage, setKycMessage] = useState('');

  useEffect(() => {
    const loadKYCInfo = async () => {
      const info = await shouldShowKYC(userProfile);
      let message = await getKYCMessage(userProfile);
      
      // If makeOptional is true, always make KYC optional regardless of country requirements
      if (makeOptional) {
        info.required = false;
        info.show = true; // Still show KYC, but as optional
        message = "Identity verification is optional. You can withdraw funds using PayPal or other available payment methods without completing KYC verification.";
      }
      
      setKycInfo(info);
      setKycMessage(message);
    };
    loadKYCInfo();
  }, [userProfile, makeOptional]);

  const getStatusIcon = () => {
    if (kycStatus?.verified) return <CheckIcon color="success" />;
    if (kycStatus?.status === 'pending') return <PendingIcon color="warning" />;
    if (kycStatus?.status === 'failed') return <ErrorIcon color="error" />;
    return <WarningIcon color="warning" />;
  };

  const getStatusColor = () => {
    if (kycStatus?.verified) return 'success';
    if (kycStatus?.status === 'pending') return 'warning';
    if (kycStatus?.status === 'failed') return 'error';
    return 'warning';
  };

  const getStatusText = () => {
  if (kycStatus?.verified) return 'Verified';
  if (kycStatus?.status === 'pending') return 'Pending Review';
  if (kycStatus?.status === 'failed') return 'Verification Failed';
  return kycStatus?.regionRequired ? 'Not Verified' : 'Opt-In Available';
  };

  if (compact) {
    return (
      <Chip
        icon={getStatusIcon()}
        label={getStatusText()}
        color={getStatusColor()}
        size="small"
        variant={kycStatus?.verified ? "filled" : "outlined"}
        sx={{ 
          fontWeight: 500,
          '& .MuiChip-icon': {
            fontSize: '1rem'
          }
        }}
      />
    );
  }

  return (
    <Paper 
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: kycStatus?.verified 
          ? 'success.main' 
          : (makeOptional ? 'info.main' : (kycInfo.required ? 'warning.main' : 'info.main')),
        background: kycStatus?.verified 
          ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)'
          : (makeOptional || !kycInfo.required)
            ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 152, 0, 0.05) 100%)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {getStatusIcon()}
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Identity Verification
        </Typography>
        <Chip
          label={getStatusText()}
          color={getStatusColor()}
          size="small"
          variant={kycStatus?.verified ? "filled" : "outlined"}
        />
      </Box>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {kycMessage}
      </Typography>

      {!kycStatus?.verified && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<VerifiedIcon />}
            onClick={onStartKYC}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            {kycStatus?.status === 'failed' ? 'Retry Verification' : 'Start Verification'}
          </Button>
          
          <Button
            variant="outlined"
            size="small"
            startIcon={<InfoIcon />}
            sx={{ color: 'text.secondary' }}
          >
            Learn More
          </Button>
        </Box>
      )}

      {kycStatus?.verified && (
        <Alert severity="success" sx={{ mt: 1 }}>
          <AlertTitle>Verification Complete</AlertTitle>
          Your identity has been successfully verified. You now have access to all features.
        </Alert>
      )}
    </Paper>
  );
};

export default KYCStatusIndicator;

